import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { venueManagement, devices, violations } from '../services/api';
import { useNavigate } from 'react-router-dom';
import {
  BuildingOfficeIcon,
  DevicePhoneMobileIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  UserGroupIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
} from '@heroicons/react/24/outline';

interface DashboardStats {
  totalVenues: number;
  totalClients: number;
  totalDevices: number;
  activeViolations: number;
  complianceRate: number;
  onlineDevices: number;
}

interface RecentActivity {
  id: string;
  type: 'violation' | 'device_offline' | 'new_client' | 'resolved';
  message: string;
  venue: string;
  timestamp: string;
}

const EngineerDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats>({
    totalVenues: 0,
    totalClients: 0,
    totalDevices: 0,
    activeViolations: 0,
    complianceRate: 0,
    onlineDevices: 0,
  });
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
  const [venues, setVenues] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      // Load venues
      const venuesResponse = await venueManagement.getMyVenues();
      setVenues(venuesResponse.data);

      // Calculate stats
      let totalDevices = 0;
      let onlineDevices = 0;
      let activeViolations = 0;
      let compliantVenues = 0;

      venuesResponse.data.forEach((venue: any) => {
        totalDevices += parseInt(venue.device_count || 0);
        onlineDevices += parseInt(venue.online_devices || 0);
        activeViolations += parseInt(venue.active_violations || 0);
        if (venue.compliance_status === 'compliant') {
          compliantVenues++;
        }
      });

      const complianceRate = venuesResponse.data.length > 0 
        ? (compliantVenues / venuesResponse.data.length) * 100 
        : 0;

      setStats({
        totalVenues: venuesResponse.data.length,
        totalClients: venuesResponse.data.filter((v: any) => v.access_level === 'admin').length,
        totalDevices,
        activeViolations,
        complianceRate,
        onlineDevices,
      });

      // Mock recent activity (in production, fetch from API)
      setRecentActivity([
        {
          id: '1',
          type: 'violation',
          message: 'SPL exceeded 95 dB threshold',
          venue: venuesResponse.data[0]?.venue_name || 'Demo Venue',
          timestamp: new Date().toISOString(),
        },
      ]);

    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'violation':
        return <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />;
      case 'device_offline':
        return <DevicePhoneMobileIcon className="h-5 w-5 text-yellow-500" />;
      case 'new_client':
        return <UserGroupIcon className="h-5 w-5 text-blue-500" />;
      case 'resolved':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Welcome Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
          Welcome back, {user?.full_name || user?.email}
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          Here's an overview of your venues and compliance status
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 mb-8">
        <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <BuildingOfficeIcon className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                    Total Venues
                  </dt>
                  <dd className="flex items-baseline">
                    <div className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                      {stats.totalVenues}
                    </div>
                    <div className="ml-2 flex items-baseline text-sm font-semibold text-green-600">
                      <ArrowTrendingUpIcon className="h-4 w-4 mr-1" />
                      Active
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 dark:bg-gray-900 px-5 py-3">
            <button
              onClick={() => navigate('/venues')}
              className="text-sm font-medium text-indigo-700 hover:text-indigo-900"
            >
              View all
            </button>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <DevicePhoneMobileIcon className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                    Total Devices
                  </dt>
                  <dd className="flex items-baseline">
                    <div className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                      {stats.totalDevices}
                    </div>
                    <div className="ml-2 text-sm text-gray-600 dark:text-gray-400">
                      ({stats.onlineDevices} online)
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 dark:bg-gray-900 px-5 py-3">
            <button
              onClick={() => navigate('/devices')}
              className="text-sm font-medium text-indigo-700 hover:text-indigo-900"
            >
              Manage devices
            </button>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ChartBarIcon className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                    Compliance Rate
                  </dt>
                  <dd className="flex items-baseline">
                    <div className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                      {stats.complianceRate.toFixed(0)}%
                    </div>
                    {stats.complianceRate >= 90 ? (
                      <CheckCircleIcon className="ml-2 h-5 w-5 text-green-500" />
                    ) : (
                      <ExclamationTriangleIcon className="ml-2 h-5 w-5 text-yellow-500" />
                    )}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 dark:bg-gray-900 px-5 py-3">
            <button
              onClick={() => navigate('/reports')}
              className="text-sm font-medium text-indigo-700 hover:text-indigo-900"
            >
              View reports
            </button>
          </div>
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
        {/* Recent Venues */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Your Venues</h3>
            <div className="space-y-3">
              {venues.slice(0, 5).map((venue) => (
                <div
                  key={venue.venue_id}
                  className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 dark:bg-gray-900 cursor-pointer"
                  onClick={() => navigate(`/venues/${venue.venue_id}`)}
                >
                  <div className="flex items-center">
                    <BuildingOfficeIcon className="h-5 w-5 text-gray-400 dark:text-gray-500 mr-3" />
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{venue.venue_name}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{venue.city}</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    {venue.compliance_status === 'compliant' ? (
                      <CheckCircleIcon className="h-5 w-5 text-green-500" />
                    ) : venue.compliance_status === 'warning' ? (
                      <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />
                    ) : (
                      <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />
                    )}
                  </div>
                </div>
              ))}
              {venues.length === 0 && (
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                  No venues yet. Create your first venue to get started.
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Recent Activity</h3>
            <div className="space-y-3">
              {recentActivity.map((activity) => (
                <div key={activity.id} className="flex items-start">
                  <div className="flex-shrink-0">
                    {getActivityIcon(activity.type)}
                  </div>
                  <div className="ml-3 flex-1">
                    <p className="text-sm text-gray-900 dark:text-gray-100">{activity.message}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {activity.venue} • {new Date(activity.timestamp).toLocaleString()}
                    </p>
                  </div>
                </div>
              ))}
              {recentActivity.length === 0 && (
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                  No recent activity
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Active Violations Alert */}
      {stats.activeViolations > 0 && (
        <div className="mt-8 bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Active Violations Detected
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <p>
                  You have {stats.activeViolations} active violation{stats.activeViolations !== 1 ? 's' : ''} across your venues.
                </p>
              </div>
              <div className="mt-4">
                <button
                  onClick={() => navigate('/reports')}
                  className="text-sm font-medium text-red-800 hover:text-red-700"
                >
                  View violations →
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EngineerDashboard;